import mapboxgl, { LngLat, Map, Marker } from "mapbox-gl";
import OverlaySetting from "../models/OverlaySetting";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GIS } from "../RobotMap";
import { createRoot, Root } from "react-dom/client";
import { Box, Typography } from "@mui/material";
import RobotIcon from "../../icons/RobotIcon";
import batteryFull from '../../../assets/batteryFull.png';
import { Robot } from "../models/Robot";
import { useSelector } from "react-redux";
import { OVERLAY_APPEND_KEY } from "../constants";
import * as turf from "@turf/turf";
import { LocationType, LocationTypeProps } from "../../../hooks/location/index.type";
import DoorPointMarker from "../_components/marker/door_point";
import LiftPointMarker from "../_components/marker/lift_point";
import useLoadRobotModelIcon from "../_icons/3d-model-loader";
import { customLog } from "../../../utils/utils";
import { show3DFeature } from "../../../utils/const";
import { Coordinates } from "../RobotMap.type";
import { getRobotType } from "../../../routes/dashboard/mock-robots";
import { current } from "@reduxjs/toolkit";
import { parseAsBoolean, useQueryState } from "nuqs";
// export const useRenderGISElementOnMap = (
//   map: React.MutableRefObject<Map | null>,
//   elements: React.ReactElement<GIS>[]
// ) => {
//   useEffect(() => {
//     if (!map.current) return;

//     const mapboxMap = map.current;
//     let markers: Marker[] = [];

//     // Create markers for each GIS element
//     elements.forEach(elem => {
//       const el = document.createElement("div");
//       el.className = "marker";
//       const marker = new Marker(el).setLngLat(elem.props.GIS).addTo(mapboxMap);
//       markers.push(marker);
//       createRoot(el).render(elem);  // Assuming createRoot and elem are correctly handled in your context
//     });

//     // make sure markers dont move when zooming
//     function updateMarkerSize() {
//       markers.forEach(marker => {
//         const zoom = mapboxMap.getZoom();
//         const scale = Math.pow(2, zoom) / 100;
//         marker.getElement()?.style.setProperty("transform", `scale(${scale})`);
//       });
//     }
//     mapboxMap.on('zoom', updateMarkerSize);


//     return () => {
//       mapboxMap.off('zoom', updateMarkerSize); // Clean up event listener
//       markers.forEach(marker => marker.remove()); // Remove markers
//     };
//   }, [elements, map]);
// };

export const useRenderGISElementOnMap = (
  map: React.MutableRefObject<Map | null>,
  elements: React.ReactElement<GIS>[],
  show = true
) => {
  const currentFloor = useSelector((state: any) => state.map.currentFloor);
  const markerRefs = useRef<Marker[]>([]);
  const markers: Marker[] = markerRefs.current;
  const savedFloorLevel = useRef(currentFloor);

  const [isPickUpFocused] = useQueryState('pickupFocused', parseAsBoolean.withDefault(false));
  const [isDropOffFocused] = useQueryState('dropoffFocused', parseAsBoolean.withDefault(false));
  const [isOpenDeliveryBoard] = useQueryState('isOpenDeliveryBoard', parseAsBoolean.withDefault(false));

  const focusedRef = useRef<"pickup" | "dropoff" | "none" | "closed">("closed");

  useEffect(() => {
    // Do not initialize until map is loaded
    if (!map.current || !show) {
      if (markers.length > 0) {
        markers.forEach(marker => marker.remove());
        markerRefs.current = [];
      }
      return;
    }
    const mapboxMap = map.current as Map;
    const currentMode = isPickUpFocused ? "pickup" : isDropOffFocused ? "dropoff" : (isOpenDeliveryBoard ? "none" : "closed");

    if (markers.length === elements.length && savedFloorLevel.current === currentFloor && currentMode === focusedRef.current) {
      markers.forEach((marker, index) => {
        marker.setLngLat(elements[index].props.GIS);
      })
      return
    }

    if (markers.length > 0) {
      // Remove all previous markers if exists
      markers.forEach(marker => marker.remove());
      markerRefs.current = [];
    }

    // Backup Plan to remove existing marker
    const markerElems = document.querySelectorAll('.gis-element-marker');
    markerElems.forEach(marker => marker.remove());

    const tmpMarkers: Marker[] = [];
    for (const elem of elements) {
      const el = document.createElement("div");
      el.className = "marker";
      el.classList.add("gis-element-marker");
      tmpMarkers.push(new Marker(el).setLngLat(elem.props.GIS).addTo(mapboxMap));
      createRoot(el).render(elem);
    }
    markerRefs.current = tmpMarkers;
    savedFloorLevel.current = currentFloor;
    customLog("checking 20", elements);

    if (isPickUpFocused) {
      focusedRef.current = "pickup";
    }
    if (isDropOffFocused) {
      focusedRef.current = "dropoff";
    }
    if (!isPickUpFocused && !isDropOffFocused) {
      focusedRef.current = isOpenDeliveryBoard ? "none" : "closed";
    }
    if (markers.length > 0) {
      // Remove all previous markers if exists
      markers.forEach(marker => marker.remove());
      markerRefs.current = [];
    }
    return () => { }

  }, [elements, map, currentFloor, show]);
};

export const useRenderPointOnMap = (
  map: React.MutableRefObject<Map | null>,
  dataArray: LocationType[],
  type: LocationTypeProps,
  show = true
) => {
  const currentFloor = useSelector((state: any) => state.map.currentFloor);

  useEffect(() => {
    // Do not initialize until map is loaded
    // Stop render when type is all
    if (!map.current || dataArray.length === 0 || type === 'all' || !show) return;
    const mapboxMap = map.current as Map;
    const markers: Marker[] = [];

    dataArray.forEach((data) => {
      // If map_level is not equal to current Floor then dont render
      if (currentFloor !== data.map[0].map_level.toString()) {
        return
      }
      const el = document.createElement("div");
      let element = undefined;
      el.classList.add("marker");
      el.classList.add(type);
      markers.push(new Marker(el).setLngLat([data.pose.longitude, data.pose.latitude]).addTo(mapboxMap));

      const pointProps = {
        name: data.location_name,
        rotation: data.pose.theta
      }
      switch (type) {
        case 'door_point':
          element = (<DoorPointMarker {...pointProps} />)
          break;

        case 'lift_point':
          element = (<LiftPointMarker {...pointProps} />)
          break;

        // TODO: Need to add other case
        default:
          return;
      }

      if (element) {
        createRoot(el).render(element);
      }
    });

    return () => {
      markers.forEach((marker) => marker.remove());
    }
  }, [dataArray, map, currentFloor, type, show])
}

let iteration = 0
const CONST_ITERATION = 5; // Maximum number of iterations before start the position validation

type RobotIconRefType = {
  id: string,
  element?: JSX.Element | null
}

export const useRenderRobotOnMap = (
  map: React.MutableRefObject<Map | null>,
  robots: Robot[],
  onRobotClick?: (robot: Robot) => void
) => {
  const markerRefs = useRef<Marker[]>([]);
  const rootRefs = useRef<Root[]>([]);
  const markers: Marker[] = markerRefs.current;

  // console.log("Rendering robots on map", robots, markers);


  const [prevRobotRef, setPrevRobotRef] = useState<Robot[]>([]);

  const handleRobotClick = useRef((robotData: Robot) => {
    onRobotClick?.(robotData);
  });

  const isSameRobots = (prevRobots: Robot[], newRobots: Robot[]) => {
    if (prevRobots.length !== newRobots.length) {
      return false;
    }

    let same = false

    prevRobots.forEach((pRobot, index) => {
      const currentRobot = newRobots[index];

      if (!(currentRobot.robotId !== pRobot.robotId ||
        currentRobot.LatLng[0] !== pRobot.LatLng[0] ||
        currentRobot.LatLng[1] !== pRobot.LatLng[1] ||
        currentRobot.direction !== pRobot.direction ||
        currentRobot.targetDirection !== pRobot.targetDirection ||
        currentRobot.batteryLevel !== pRobot.batteryLevel ||
        currentRobot.visibility !== pRobot.visibility ||
        currentRobot.connectivity !== pRobot.connectivity ||
        currentRobot.robotName !== pRobot.robotName ||
        currentRobot.map_uuid !== pRobot.map_uuid ||
        currentRobot.layout_uuid !== pRobot.layout_uuid ||
        currentRobot.north !== pRobot.north ||
        currentRobot.robotSerialNumber !== pRobot.robotSerialNumber
      )) {
        // same = true
      }
    });
    return same;
  }

  useEffect(() => {
    // Do not initialize until map is loaded
    if (!map.current) return;

    // if (markers.length > 0 && !!prevRobotRef && prevRobotRef.length > 0 && iteration > CONST_ITERATION) {
    //   // If robots are not changed, do not re-render markers
    //   const isSameAsPrev = prevRobotRef.map((prev) => {
    //     const currentRobot = robots.find((r) => r.robotId === prev.robotId);
    //     if (!currentRobot) {
    //       return false;
    //     }
    //     return (
    //       prev.LatLng[0] === currentRobot.LatLng[0] &&
    //       prev.LatLng[1] === currentRobot.LatLng[1] &&
    //       prev.direction === currentRobot.direction &&
    //       prev.targetDirection === currentRobot.targetDirection &&
    //       prev.batteryLevel === currentRobot.batteryLevel &&
    //       prev.visibility === currentRobot.visibility &&
    //       prev.connectivity === currentRobot.connectivity &&
    //       prev.robotName === currentRobot.robotName &&
    //       prev.map_uuid === currentRobot.map_uuid &&
    //       prev.layout_uuid === currentRobot.layout_uuid &&
    //       prev.north === currentRobot.north &&
    //       prev.robotSerialNumber === currentRobot.robotSerialNumber
    //     );
    //   }).filter((d) => d === false).length === 0;

    //   if (isSameAsPrev && prevRobotRef.length === robots.length) {
    //     return () => { }
    //   }
    // }

    // Check Duplicate Rendered Robots
    const renderedRobots = document.querySelectorAll('.robot-marker');
    let duplicateFound = false;
    markers.forEach(element => {
      const markerRobotId = element.getElement()?.getAttribute("robotId");
      const findRobotIds = Array.from(renderedRobots).filter(r => r.getAttribute("robotId") === markerRobotId);
      if (findRobotIds.length > 1) {
        // Remove the first one
        findRobotIds[0].remove();

        duplicateFound = true;
      }
    });

    // If Everything is same
    if (markers.length > 0 && !!prevRobotRef && isSameRobots(prevRobotRef, robots) && !duplicateFound) {
      return
    }


    if (markers.length > 0 && prevRobotRef.length === robots.length && !duplicateFound) {
      // move the markers instead of removing them
      let resetGenerateMarker = false
      markers.forEach((marker) => {
        const robot = robots.find(r => r.robotId === marker.getElement()?.getAttribute("robotId"));

        if (!!robot) {
          marker.setLngLat(robot.LatLng);
          marker.setRotation(robot.direction);
        } else {
          resetGenerateMarker = true
        }
      });
      iteration++;
      if (!resetGenerateMarker) {
        return
      }
    }

    // Remove all previous markers if exists
    if (markers.length > 0 || duplicateFound) {
      markers.forEach(marker => marker.remove());
      // Backup plan if cant delete marker
      const robotElems = document.querySelectorAll('.robot-marker')
      robotElems.forEach((elem) => elem.remove())
    }
    const mapboxMap = map.current as Map;
    const tmpNewMarker: Marker[] = []
    const tmpRootRefs: Root[] = [];

    for (const robot of robots) {
      const el = document.createElement("div");
      el.className = "marker";
      el.classList.add("robot-marker")
      el.setAttribute("robotId", robot.robotId);

      const root = createRoot(el);
      tmpRootRefs.push(root);
      const robotIcon = (
        <RobotIcon
          robot={{
            name: robot.robotName,
            status: robot.status,
            battery: robot.batteryLevel,
            url: robot.url
          }}
        />
      )

      root.render(
        <Box className="robot-box" onClick={() => handleRobotClick.current?.(robot)}>
          {robotIcon}
        </Box>
      );

      tmpNewMarker.push(new Marker(el).setLngLat(robot.LatLng).addTo(mapboxMap));
    }
    // prevRobotRef.current = [...robots]
    setPrevRobotRef([...robots])
    markerRefs.current = tmpNewMarker;
    rootRefs.current = tmpRootRefs;
    iteration++;
  }, [robots, map]);

  useEffect(() => {
    if (!markerRefs.current || markerRefs.current.length === 0 || !robots || robots.length === 0) {
      return
    }

    let requestId: number;
    function animateMarker() {
      const currentBearing = map.current?.getBearing() || 0;

      for (let i = 0; i < markerRefs.current.length; i++) {
        if (robots.length > 0 && !!robots[i] && robots[i].LatLng[0] !== 0) {

          // Handle Animate Movement
          // const markerAsAny = markerRefs.current[i] as any;
          // const _isAnimating = markerAsAny._isAnimating
          // const _targetPosition = markerAsAny._targetPosition
          // const _startPosition = markerAsAny._startPosition
          // const _startTime = markerAsAny._startTime

          let direction = robots[i].direction;
          let north = robots[i].north;

          // const startOrientation = markerAsAny._startRotation || robots[i].direction;

          // if (_isAnimating && !!_targetPosition && !!_startPosition && !!_startTime) {
          //   // if still animating, then just continue the existing animation first

          //   const elapsed = Date.now() - _startTime;
          //   if (elapsed >= 1000) {
          //     // Assume close to destination
          //     markerRefs.current[i].setLngLat(robots[i].LatLng);
          //     markerAsAny._isAnimating = false;
          //     markerAsAny._startTime = 0;
          //     markerAsAny._startPosition = undefined;
          //     markerAsAny._targetPosition = undefined;

          //     direction = robots[i].direction;
          //     markerAsAny._currentRotation = direction; // Set the orientation difference for animation

          //     north = robots[i].north;
          //   } else {

          //     const progress = elapsed / 1000;
          //     const rotationProgress = Math.min(progress * 2, 1); // Adjust rotation speed
          //     const easedProgress = easeInOutCubic(progress);

          //     const easedRotation = easeInOutCubic(rotationProgress);

          //     const currentLng = _startPosition[0] + (_targetPosition[0] - _startPosition[0]) * easedProgress;
          //     const currentLat = _startPosition[1] + (_targetPosition[1] - _startPosition[1]) * easedProgress;

          //     markerRefs.current[i].setLngLat([currentLng, currentLat]);

          //     const endOrientation = calculateRobotOrientation(markerAsAny._startPosition, markerAsAny._targetPosition);
          //     const currentOrientation = startOrientation + (endOrientation - startOrientation) * easedRotation;
          //     // Calculate direction based on startPosition and endPosition Coordinates
          //     direction = ((currentOrientation % 360) + 360) % 360; // Normalize to [0, 360)
          //     markerAsAny._currentRotation = direction; // Set the orientation difference for animation

          //     north = direction * -1;
          //   }
          // } else {
          //   const currentLngLat = markerAsAny.getLngLat();
          //   if (currentLngLat.lng !== robots[i].LatLng[0] || currentLngLat.lat !== robots[i].LatLng[1]) {
          //     markerAsAny._targetPosition = [robots[i].LatLng[0], robots[i].LatLng[1]];
          //     markerAsAny._startPosition = [currentLngLat.lng, currentLngLat.lat];
          //     markerAsAny._startTime = Date.now();
          //     markerAsAny._isAnimating = true;
          //   }
          // }

          const robot = robots[i];
          // console.log("HANDS Anim", robot.url);
          const robotIcon = (
            <RobotIcon
              robot={{
                name: robot.robotName,
                status: robot.status,
                battery: robot.batteryLevel,
                url: robot.url
              }}
            />
          )

          rootRefs.current[i].render(
            <Box className="robot-box" onClick={() => handleRobotClick.current?.(robot)}>
              {robotIcon}
            </Box>
          )

          const adjustedRotation = (direction - currentBearing);
          const adjustedNorth = north + currentBearing
          markerRefs.current[i].setLngLat(robots[i].LatLng)
          markerRefs.current[i].setRotation(adjustedRotation);
          // Rotate the robot icon to be upright
          markerRefs.current[i]
            .getElement()
            ?.children.item(0)
            ?.children.item(0)
            ?.children.item(0)
            ?.setAttribute("style", `transform: rotate(${adjustedNorth}deg)`);
          // make sure robot is always on top
          markerRefs.current[i].getElement()?.style.setProperty("z-index", "1000");
          markerRefs.current[i].getElement()?.style.setProperty("visibility", (robot.visibility ? "visible" : "hidden"));
          markerRefs.current[i].getElement()?.style.setProperty("opacity", (robot.visibility ? "1" : "0"));
        }
      }
      requestId = requestAnimationFrame(animateMarker);
    }
    requestId = requestAnimationFrame(animateMarker);
    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [markerRefs.current])
};

const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export const useLegacyRenderRobotOnMap = (
  map: React.MutableRefObject<Map | null>,
  robots: Robot[],
  onRobotClick?: (robot: Robot) => void
) => {
  const iconType = useSelector((state: any) => state.map.iconType);
  const isShow3DFeature = show3DFeature && iconType === '3D'

  const initMarkers = () => {
    // Do not initialize until map is loaded
    if (!map.current || isShow3DFeature) return;
    customLog("checking 22", robots);
    var markers: Marker[] = [];
    const mapboxMap = map.current as Map;
    for (const robot of robots) {
      customLog("checking 23", robot);
      const el = document.createElement("div");
      el.className = "marker";
      el.classList.add("robot-marker")
      el.setAttribute("robotId", robot.robotId);
      createRoot(el).render(
        <Box onClick={() => onRobotClick && onRobotClick(robot)}>
          <RobotIcon
            robot={{
              url: robot.url,
              name: robot.robotName,
              status: robot.status,
              battery: robot.batteryLevel,
            }}
          />
        </Box>
      );

      markers.push(new Marker(el).setLngLat(robot.LatLng).addTo(mapboxMap));
    }

    let requestId: number;
    function animateMarker(timestamp: any) {
      if (isShow3DFeature) {
        cancelAnimationFrame(requestId);
        return;
      }
      for (let i = 0; i < markers.length; i++) {
        markers[i].setLngLat(robots[i].LatLng);
        markers[i].setRotation(robots[i].direction);
        // Rotate the robot icon to be upright
        markers[i]
          .getElement()
          ?.children.item(0)
          ?.children.item(0)
          ?.children.item(0)
          ?.setAttribute("style", `transform: rotate(${robots[i].north}deg)`);
        // make sure robot is always on top
        markers[i].getElement()?.style.setProperty("z-index", "1000");
        markers[i].getElement()?.style.setProperty("visiblity", (robots[i].visibility ? "visible" : "hidden"));
        markers[i].getElement()?.style.setProperty("opacity", (robots[i].visibility ? "1" : "0"));
      }

      requestId = requestAnimationFrame(animateMarker);
    }
    requestId = requestAnimationFrame(animateMarker);

    return () => {
      cancelAnimationFrame(requestId);
      markers.forEach((marker) => marker.remove());
    };
  }

  useEffect(() => {
    if (isShow3DFeature) {
      // If icon type is 3D, we can initialize markers
      return
    }
    return initMarkers();
  }, [robots, map, iconType]);

  return {
    initMarkers
  };
};

export const useRenderLinesOnMap = (
  map: React.MutableRefObject<Map | null>,
  line: [number, number][],
  lineWidth: number = 8,
  lineColor: string = "#888"
) => {
  const lineRef = useRef<[number, number][]>([]);
  const lineWidthRef = useRef<number>(8)
  const lineColorRef = useRef<string>("#888")

  const isSameValue = () => {
    if (JSON.stringify(line) !== JSON.stringify(lineRef.current)) {
      return false
    }

    if (lineWidth !== lineWidthRef.current) {
      return false;
    }

    if (lineColor !== lineColorRef.current) {
      return false;
    }

    return true
  }

  useEffect(() => {
    const mapboxMap = map.current as Map;
    if (!map.current || line.length === 0) {
      if (lineRef.current.length > 0) {
        mapboxMap.removeLayer("route");
        mapboxMap.removeSource("route");
        lineRef.current = []
      }
      return
    }

    if (lineRef.current.length > 0) {
      if (isSameValue()) {
        return;
      }
      mapboxMap.removeLayer("route");
      mapboxMap.removeSource("route");
    }

    mapboxMap.addSource("route", {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: line,
        },
      },
    });
    mapboxMap.addLayer({
      id: "route",
      type: "line",
      source: "route",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      slot: 'top',
      paint: {
        "line-color": lineColor,
        "line-width": lineWidth,
      },
    });

    lineRef.current = line
    lineColorRef.current = lineColor
    lineWidthRef.current = lineWidth
    // return () => {
    //   mapboxMap.removeLayer("route");
    //   mapboxMap.removeSource("route");
    // };
  }, [line, map, lineColor, lineWidth]);
};


export const calculateScaledCoordinates = (coordinates: number[][], scaleFactor: number): Coordinates => {
  const centroid = turf.centroid({
    type: "Polygon",
    coordinates: [coordinates]
  }).geometry.coordinates

  // Scale the coordinates
  const scaledCoordinates = coordinates.map(coord => [
    centroid[0] + (coord[0] - centroid[0]) * scaleFactor,
    centroid[1] + (coord[1] - centroid[1]) * scaleFactor
  ]);

  return scaledCoordinates as Coordinates;
};

export const calculateRotatedCoordinates = (coords: number[][], angle: number) => {
  const rotatedPolygon = turf.transformRotate({
    type: "Polygon",
    coordinates: [coords]
  }, angle);
  return (turf.getCoords(rotatedPolygon))[0].slice(0, 4);
};

export const getCoordinates = (topLeftLng: number, topLeftLat: number, width: number, height: number): number[][] => {
  return [
    [topLeftLng, topLeftLat],
    [topLeftLng + width, topLeftLat],
    [topLeftLng + width, topLeftLat - height],
    [topLeftLng, topLeftLat - height],
  ];
}

export const loadOverlay = (
  map: React.MutableRefObject<Map | null>,
  overlaySetting: OverlaySetting,
  name: string,
  onSelectMap?: ({ overlaySetting, name }: { overlaySetting: OverlaySetting, name: string }) => void
) => {
  try {
    const mapboxMap = map.current as Map;

    const currentCoordinates = [
      [overlaySetting.topLeftLng, overlaySetting.topLeftLat],
      [
        overlaySetting.topLeftLng + overlaySetting.widthInGIS,
        overlaySetting.topLeftLat,
      ],
      [
        overlaySetting.topLeftLng + overlaySetting.widthInGIS,
        overlaySetting.topLeftLat - overlaySetting.heightInGIS,
      ],
      [
        overlaySetting.topLeftLng,
        overlaySetting.topLeftLat - overlaySetting.heightInGIS,
      ],
    ];
    customLog("Checking Current Coordinates", name, currentCoordinates);
    const coordinates = (overlaySetting.rotation ?
      calculateRotatedCoordinates(
        currentCoordinates, overlaySetting.rotation
      ) : currentCoordinates);

    overlaySetting.coordinates = coordinates;

    mapboxMap.addSource(name, {
      type: "image",
      url: overlaySetting.url,
      coordinates: coordinates,
    });

    mapboxMap.addLayer({
      id: name,
      type: "raster",
      source: name,
      slot: 'bottom',
      paint: {
        "raster-fade-duration": 0,
        "raster-opacity": (overlaySetting.opacity / 100)
      }
    });

    // TMP DISABLE MAP
    // const geometryData =
    // {
    //   type: 'geojson' as 'geojson',
    //   data: {
    //     properties: {},
    //     type: 'Feature' as 'Feature',
    //     geometry: {
    //       type: 'Polygon' as 'Polygon',
    //       coordinates: [coordinates]
    //     }
    //   }
    // }

    // mapboxMap.addSource(name + OVERLAY_APPEND_KEY, geometryData)
    // mapboxMap.addLayer({
    //   id: name + OVERLAY_APPEND_KEY,
    //   type: 'fill',
    //   "source": name + OVERLAY_APPEND_KEY,
    //   paint: {
    //     "fill-color": '#1976d2',
    //     "fill-opacity": 0,
    //   }
    // })

    // mapboxMap.on("mousedown", name + OVERLAY_APPEND_KEY, (e) => {
    // overlaySetting.dragging = true
    // overlaySetting.startCoordinates = [e.lngLat.lng, e.lngLat.lat]
    // mapboxMap.dragPan.disable()
    // mapboxMap.doubleClickZoom.disable();
    // onSelectMap?.({ overlaySetting, name})
    // e.preventDefault()
    // });

    // TMP DISABLE
    // mapboxMap.on("mouseenter", name + OVERLAY_APPEND_KEY, (e) => {
    //   if (!overlaySetting.dragging) {
    //     mapboxMap.getCanvas().style.cursor = 'pointer';
    //   }
    // })

    // mapboxMap.on("mouseleave", name + OVERLAY_APPEND_KEY, () => {
    //   mapboxMap.getCanvas().style.cursor = '';
    // })

    // mapboxMap.on("mousemove", (e) => {
    //   mapboxMap.doubleClickZoom.disable();
    //   if (overlaySetting.dragging && overlaySetting.startCoordinates && overlaySetting.coordinates && overlaySetting.coordinates.length > 0) {
    //     e.preventDefault()

    //     // const coords = e.lngLat;
    //     mapboxMap.getCanvas().style.cursor = 'grabbing';

    //     // Count the drag distance
    //     const dx = (e.lngLat.lng - overlaySetting.startCoordinates[0]);
    //     const dy = (e.lngLat.lat - overlaySetting.startCoordinates[1]);

    //     // set the new coordinate after addition in (x & y) coordinate
    //     const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]);

    //     // geometryData.data.geometry.coordinates = [newCoordinates];
    //     (mapboxMap.getSource(name) as mapboxgl.ImageSource).setCoordinates(newCoordinates);
    //     (mapboxMap.getSource(name + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
    //       type: "Feature",
    //       properties: {},
    //       geometry: {
    //         type: 'Polygon',
    //         coordinates: [newCoordinates],
    //       },
    //     })
    //   }
    // })


    // const onStopDragging = (lngLat?: number[]) => {
    //   mapboxMap.doubleClickZoom.enable();
    //   if (overlaySetting.dragging) {      mapboxMap.dragPan.enable()
    //     overlaySetting.dragging = false
    //     if (lngLat && overlaySetting.startCoordinates && overlaySetting.coordinates) {
    //       const dx = (lngLat[0] - overlaySetting.startCoordinates[0]);
    //       const dy = (lngLat[1] - overlaySetting.startCoordinates[1]);
    //       // set the new coordinate after addition in (x & y) coordinate
    //       const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]);
    //       overlaySetting.coordinates = newCoordinates;
    //     }
    //     overlaySetting.startCoordinates = undefined
    //     mapboxMap.getCanvas().style.cursor = '';
    //   }
    // }

    // mapboxMap.on("mouseup", name + OVERLAY_APPEND_KEY, (e) => {
    //   onStopDragging([e.lngLat.lng, e.lngLat.lat])
    // })

    // mapboxMap.on("click", (e) => {
    //   if (overlaySetting.dragging) {
    //     onStopDragging([e.lngLat.lng, e.lngLat.lat])
    //   }
    // })
  } catch (error) { }
};

export const loadOverlayByCoordinate = (
  map: React.MutableRefObject<Map | null>,
  overlaySetting: OverlaySetting,
  name: string,
  onSelectMap?: ({ overlaySetting, name }: { overlaySetting: OverlaySetting, name: string }) => void
) => {
  try {
    const mapboxMap = map.current as Map;

    const currentCoordinates = overlaySetting.coordinates || [
      [overlaySetting.topLeftLng, overlaySetting.topLeftLat],
      [
        overlaySetting.topLeftLng + overlaySetting.widthInGIS,
        overlaySetting.topLeftLat,
      ],
      [
        overlaySetting.topLeftLng + overlaySetting.widthInGIS,
        overlaySetting.topLeftLat - overlaySetting.heightInGIS,
      ],
      [
        overlaySetting.topLeftLng,
        overlaySetting.topLeftLat - overlaySetting.heightInGIS,
      ],
    ];

    const coordinates = (overlaySetting.rotation ?
      calculateRotatedCoordinates(currentCoordinates, overlaySetting.rotation) : currentCoordinates);

    overlaySetting.coordinates = coordinates;

    mapboxMap.addSource(name, {
      type: "image",
      url: overlaySetting.url,
      coordinates: coordinates,
    });

    mapboxMap.addLayer({
      id: name,
      type: "raster",
      source: name,
      layout: {
        "visibility": "none"
      },
      paint: {
        "raster-fade-duration": 0,
        "raster-opacity": (overlaySetting.opacity / 100)
      }
    });
  } catch (error) { }
};

export const removeOverlay = (
  map: React.MutableRefObject<Map | null>,
  name: string
) => {
  try {
    const mapboxMap = map.current as Map;
    mapboxMap.removeLayer(name);
    mapboxMap.removeSource(name);
  } catch (error) {

  }
}

